import AWS from 'aws-sdk'
import { AttributeDefinitions, AttributeMap, GlobalSecondaryIndexList, KeySchema, ProvisionedThroughput, CreateTableInput } from "aws-sdk/clients/dynamodb"
import { KeyParams, RangeParams, TextParams } from "./types"
import DocumentClient = AWS.DynamoDB.DocumentClient
import QueryInput = AWS.DynamoDB.DocumentClient.QueryInput
import { Sort, TextFilter, ValueFilter, RangeFilter, ExistsFilter, TransactWriteOperation } from './types2'

export class ParamsProvider {
    constructor(
        readonly tenantKey: string,
        readonly uniqueKey: string
    ) {}

    scanParams = (
        tableName: string,
        startKey?: KeyParams,
        limit?: number,
    ) => {
        return {
            TableName: tableName,
            ...(startKey && {
                ExclusiveStartKey: startKey
            }),
            ...(limit && {
                Limit: limit
            })
        }
    }

    queryParams = (
        tableName: string,
        tKeyValue: string,
        startKey?: KeyParams,
        limit?: number,
        propName?: string,
        paramsFilter?: KeyParams,
        rangeFilters?: RangeParams,
        existingParamsFilter?: string[],
        selectCount?: boolean
    ) => {
        const expressionAttributeNames: TextParams = {}
        const expressionAttributeValues: KeyParams = {}
        const filterExpressions = []

        const tKeyAttributeName = `#${this.tenantKey}`
        const tKeyAttributeValue = ':tKey'
        expressionAttributeNames[tKeyAttributeName] = this.tenantKey
        expressionAttributeValues[tKeyAttributeValue] = tKeyValue

        if (paramsFilter) {
            for (const [prop, value] of Object.entries(paramsFilter)) {
                const isArray = Array.isArray(value)
                if (isArray && value.length > 0) {
                    const attributeValues = value.map((val, index) => {
                        const attributeValue = `:${prop}${index}`
                        expressionAttributeValues[attributeValue] = val
                        return attributeValue
                    })
                    
                    const attributeName = `#${prop}`
                    expressionAttributeNames[attributeName] = prop
                    filterExpressions.push(`${attributeName} IN (${attributeValues.join(', ')})`)
                } else if (!isArray) {
                    const attributeName = `#${prop}`
                    const attributeValue = `:${prop}`
                    expressionAttributeNames[attributeName] = prop
                    expressionAttributeValues[attributeValue] = value
                    let filterExpression = `${attributeName} = ${attributeValue}`
                    if (value === false) {
                        filterExpression = filterExpression + ` OR attribute_not_exists(${attributeName})`
                    }
                    filterExpressions.push(filterExpression)
                }
            }
        }

        if (rangeFilters) {
            for (const [prop, range] of Object.entries(rangeFilters)) {
                const attributeName = `#${prop}`
                const startValue = `:${prop}Start`
                const endValue = `:${prop}End`
                expressionAttributeNames[attributeName] = prop
                expressionAttributeValues[startValue] = range.start
                expressionAttributeValues[endValue] = range.end
                filterExpressions.push(`${attributeName} BETWEEN ${startValue} AND ${endValue}`)
            }
        }

        if (existingParamsFilter) {
            for (const existingProp of existingParamsFilter) {
                filterExpressions.push(`attribute_exists(${existingProp})`)
            }
        }

        const filterExpression = filterExpressions.join(' AND ')

        return {
            TableName: tableName,
            KeyConditionExpression: `${tKeyAttributeName} = ${tKeyAttributeValue}`,
            ...(filterExpression && {
                FilterExpression: filterExpression
            }),
            ExpressionAttributeNames: expressionAttributeNames,
            ExpressionAttributeValues: expressionAttributeValues,
            ...(propName && {
                ProjectionExpression: propName
            }),
            ...(startKey && {
                ExclusiveStartKey: startKey
            }),
            ...(limit && {
                Limit: limit
            }),
            ...(selectCount && {
                Select: 'COUNT'
            })
        }
    }

    queryParams2 = (
        tableName: string,
        tKeyValue: string,
        startKey?: DocumentClient.Key,
        limit?: number,
        sort?: Sort,
        textFilters?: TextFilter[],
        valueFilters?: ValueFilter[],
        rangeFilters?: RangeFilter[],
        existsFilters?: ExistsFilter[],
        count?: boolean
    ): QueryInput => {
        const tKeyAttrName = `#tenantKey`
        const tKeyAttrValue = `:${this.tenantKey}`
        
        const ExpressionAttributeNames: Record<string, string> = {
            [tKeyAttrName]: this.tenantKey
        }
        const ExpressionAttributeValues: Record<string, string | boolean> = {
            [tKeyAttrValue]: tKeyValue
        }

        const keyConditionExpressions: string[] = [
            `${tKeyAttrName} = ${tKeyAttrValue}`
        ]
    
        let IndexName, ScanIndexForward
        if (sort) {
            IndexName = sort.index
            ScanIndexForward = sort.asc ?? true
        }
    
        let Limit
        if (limit !== undefined) {
            Limit = limit
        }
    
        let ExclusiveStartKey
        if (startKey) {
            ExclusiveStartKey = startKey
        }
    
        const filterExpressions: string[] = []
    
        if (textFilters && textFilters.length > 0) {
            textFilters.forEach((filter, i) => {
                const propAttrName = `#textProp${i + 1}`
                const valueAttrName = `:textValue${i + 1}`
                ExpressionAttributeNames[propAttrName] = filter.prop
                ExpressionAttributeValues[valueAttrName] = filter.text
                filterExpressions.push(`contains(${propAttrName}, ${valueAttrName})`)
            })
        }
    
        if (valueFilters && valueFilters.length > 0) {
            valueFilters.forEach((filter, i) => {
                const propAttrName = `#valueProp${i + 1}`
                const valueAttrName = `:valueValue${i + 1}`
                ExpressionAttributeNames[propAttrName] = filter.prop
                ExpressionAttributeValues[valueAttrName] = filter.value
                const operator = filter.equals === false ? '<>' : '='
                const expressionList = filter.prop === sort?.prop ? keyConditionExpressions : filterExpressions
                expressionList.push(`${propAttrName} ${operator} ${valueAttrName}`)
            })
        }
    
        if (rangeFilters && rangeFilters.length > 0) {
            rangeFilters.forEach((filter, i) => {
                const propAttrName = `#rangeProp${i + 1}`
                const startValueAttrName = `:rangeStartValue${i + 1}`
                const endValueAttrName = `:rangeEndValue${i + 1}`
                ExpressionAttributeNames[propAttrName] = filter.prop
                let expression
                if (filter.start && filter.end) {
                    ExpressionAttributeValues[startValueAttrName] = filter.start
                    ExpressionAttributeValues[endValueAttrName] = filter.end
                    expression = `${propAttrName} BETWEEN ${startValueAttrName} AND ${endValueAttrName}`
                } else if (filter.start) {
                    ExpressionAttributeValues[startValueAttrName] = filter.start
                    expression = `${propAttrName} >= ${startValueAttrName}`
                } else if (filter.end) {
                    ExpressionAttributeValues[endValueAttrName] = filter.end
                    expression = `${propAttrName} <= ${endValueAttrName}`
                }
                const expressionList = filter.prop === sort?.prop ? keyConditionExpressions : filterExpressions
                expression && expressionList.push(expression)
            })
        }
    
        if (existsFilters && existsFilters.length > 0) {
            existsFilters.forEach((filter, i) => {
                const propAttrName = `#existsProp${i + 1}`
                ExpressionAttributeNames[propAttrName] = filter.prop
                filterExpressions.push(
                    filter.exists
                        ? `attribute_exists(${propAttrName})`
                        : `attribute_not_exists(${propAttrName})`
                )
            })
        }

        const KeyConditionExpression = keyConditionExpressions.join(' AND ')
    
        let FilterExpression
        if (filterExpressions.length > 0) {
            FilterExpression = filterExpressions.join(' AND ')
        }
    
        return {
            TableName: tableName,
            KeyConditionExpression,
            ExpressionAttributeNames,
            ExpressionAttributeValues,
            ...(IndexName && {
                IndexName
            }),
            ...(ScanIndexForward !== undefined && {
                ScanIndexForward
            }),
            ...(Limit && {
                Limit
            }),
            ...(ExclusiveStartKey && {
                ExclusiveStartKey
            }),
            ...(FilterExpression && {
                FilterExpression
            }),
            ...(count && {
                Select: 'COUNT'
            })
        }
    }
    

    getParams = (tableName: string, uKeyValue: string, tKeyValue?: string) => ({
        TableName: tableName,
        Key: tKeyValue ? {
            [this.tenantKey]: tKeyValue,
            [this.uniqueKey]: uKeyValue
        } : {
            [this.uniqueKey]: uKeyValue
        }
    })

    batchGetParams = (tableName: string, uKeyValues: string[], tKeyValue?: string) => ({
        RequestItems: {
            [tableName]: {
                Keys: uKeyValues.map(uKeyValue => ({
                    [this.tenantKey]: tKeyValue,
                    [this.uniqueKey]: uKeyValue
                }))
            }
        }
    })

    batchCreateOrUpdateParams = (tableName: string, list: AttributeMap[]) => ({
        RequestItems: {
            [tableName]: list.map(item => ({
                PutRequest: {
                    Item: item
                }
            }))
        }
    })

    deleteParams = (tableName: string, uKeyValue: string, tKeyValue?: string) => ({
        TableName: tableName,
        Key: {
            [this.tenantKey]: tKeyValue,
            [this.uniqueKey]: uKeyValue
        }
    })

    batchDeleteParams = (tableName: string, uKeyValues: string[], tKeyValue?: string) => ({
        RequestItems: {
            [tableName]: uKeyValues.map(uKeyValue => ({
                DeleteRequest: this.deleteParams(tableName, uKeyValue, tKeyValue)
            }))
        }
    })

    transactWriteParams = (operations: TransactWriteOperation[], tKeyValue: string): DocumentClient.TransactWriteItemsInput => {
        const key = (id: string) => ({
            [this.tenantKey]: tKeyValue,
            [this.uniqueKey]: id
        })
        const existsCondition = (mustExist?: boolean) => mustExist ? {
            ConditionExpression: 'attribute_exists(#uKey)',
            ExpressionAttributeNames: { '#uKey': this.uniqueKey }
        } : {}

        const transactItems = operations.map((operation): DocumentClient.TransactWriteItem => {
            if (operation.type === 'put') {
                const expectedProps = Object.keys(operation.expectedValues || {})
                return {
                    Put: {
                        TableName: operation.dbTableName,
                        Item: operation.item,
                        ...(expectedProps.length > 0 && {
                            ConditionExpression: expectedProps.map((_, index) => `#e${index} = :e${index}`).join(' AND '),
                            ExpressionAttributeNames: Object.fromEntries(expectedProps.map((prop, index) => [`#e${index}`, prop])),
                            ExpressionAttributeValues: Object.fromEntries(expectedProps.map((prop, index) => [`:e${index}`, operation.expectedValues![prop]]))
                        })
                    }
                }
            } else if (operation.type === 'delete') {
                return {
                    Delete: {
                        TableName: operation.dbTableName,
                        Key: key(operation.id),
                        ...existsCondition(operation.mustExist)
                    }
                }
            } else {
                const incrementProps = Object.keys(operation.increments)
                const condition = existsCondition(operation.mustExist)
                return {
                    Update: {
                        TableName: operation.dbTableName,
                        Key: key(operation.id),
                        UpdateExpression: `ADD ${incrementProps.map((_, index) => `#i${index} :i${index}`).join(', ')}`,
                        ...(condition.ConditionExpression && { ConditionExpression: condition.ConditionExpression }),
                        ExpressionAttributeNames: {
                            ...condition.ExpressionAttributeNames,
                            ...Object.fromEntries(incrementProps.map((prop, index) => [`#i${index}`, prop]))
                        },
                        ExpressionAttributeValues: Object.fromEntries(incrementProps.map((prop, index) => [`:i${index}`, operation.increments[prop]]))
                    }
                }
            }
        })

        return { TransactItems: transactItems }
    }

    createTablesParams = (tableName: string, hasTKey: boolean, secondaryIndexes?: string[]): CreateTableInput => {
        const keySchema: KeySchema = []
        if (hasTKey) {
            keySchema.push({ AttributeName: this.tenantKey, KeyType: 'HASH' })
        }
        keySchema.push({ AttributeName: this.uniqueKey, KeyType: 'RANGE' })

        const attributeDefinitions: AttributeDefinitions = []
        if (hasTKey) {
            attributeDefinitions.push({ AttributeName: this.tenantKey, AttributeType: 'S' })
        }
        attributeDefinitions.push({ AttributeName: this.uniqueKey, AttributeType: 'S' })
        
        const provisionedThroughput: ProvisionedThroughput = {
            ReadCapacityUnits: 1,
            WriteCapacityUnits: 1,
        }

        const globalSecondaryIndexes: GlobalSecondaryIndexList = []
        if (secondaryIndexes) {
            secondaryIndexes.forEach(index => {
                attributeDefinitions.push({ AttributeName: index, AttributeType: 'S' })
                globalSecondaryIndexes.push({
                    IndexName: index,
                    KeySchema: [
                        { AttributeName: index, KeyType: 'HASH' }
                    ],
                    Projection: {
                        ProjectionType: 'ALL'
                    },
                    ProvisionedThroughput: provisionedThroughput
                })
            })
        }

        return {
            TableName: tableName,
            KeySchema: keySchema,
            AttributeDefinitions: attributeDefinitions,
            ...(globalSecondaryIndexes.length > 0 && {
                GlobalSecondaryIndexes: globalSecondaryIndexes
            }),
            ProvisionedThroughput: provisionedThroughput
        }
    }

    deleteTableParams = (tableName: string) => {
        return {
            TableName: tableName,
        }
    }
}
